// pages/TrackMed2.js - FIXED for mobile view
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getMedications,
  addMedication,
  updateMedication,
  takeMedication,
  deleteMedication,
  formatTimeWithDate,
  calculateTimeRemaining,
  calculateNextDose,
  calculateTimeSinceLastTaken,
  formatTimeSince,
  getFormattedHistory,
  getFrequencyDescription,
  safeToDate,
  formatTime,
  getCurrentTime12Hour
} from './services/medicationservice2';
import PrescriptionInfo from './components/PrescriptionInfo';
// Import Header with error handling
import Header from './components/Header';
import './TrackMed.css';
import { testNotification } from './services/medicationservice2';
import { getFcmToken } from './firebase';
import { useAuth } from './context/AuthContext';
import { initNotifications } from './services/Notify';
const TrackMed2 = () => {
  const { currentUser } = useAuth();
  const [reminders, setReminders] = useState({
    before30Min: true,
    before10Min: true,
    atExactTime: true
  });
  const [medications, setMedications] = useState([]);
  const [medName, setMedName] = useState('');
  const [prescription, setPrescription] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [selectedMedId, setSelectedMedId] = useState(null);
  const [customTime, setCustomTime] = useState('');
  const [isTakingNow, setIsTakingNow] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [expandedCards, setExpandedCards] = useState({});
  const [currentTime, setCurrentTime] = useState(getCurrentTime12Hour());
  const [isLogging, setIsLogging] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMedicationName, setSuccessMedicationName] = useState('');
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState({ enabled: false, subscribed: false });
  const [isMobile, setIsMobile] = useState(false);
  const [headerError, setHeaderError] = useState(false); // Add header error state

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle Header component error
  useEffect(() => {
    // If Header fails to load, we'll still show the rest of the app
    const timer = setTimeout(() => {
      if (!document.querySelector('header')) {
        setHeaderError(true);
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  // Calculate medication statistics
  const medicationStats = useMemo(() => {
    let taken = 0;
    let missed = 0;
    let notStarted = 0;
    
    medications.forEach(med => {
      if (med.lastTaken) {
        const timeRemaining = calculateTimeRemaining(med.nextDose);
        if (timeRemaining.isOverdue) {
          missed++;
        } else {
          taken++;
        }
      } else {
        notStarted++;
      }
    });
    
    return { taken, missed, notStarted, total: medications.length };
  }, [medications]);

  const subscribeToNotifications = async () => {
    const result = await initNotifications(currentUser);
    if (result.granted && result.token) {
      setNotificationStatus({ enabled: true, subscribed: true });
    } else {
      setNotificationStatus({ enabled: false, subscribed: false });
    }
  };
  // Load medications from Firebase
  const loadMedications = useCallback(async () => {
    try {
      setLoading(true);
      const meds = await getMedications();
      setMedications(meds);
      setError('');
    } catch (err) {
      console.error('Error loading medications:', err);
      setError('Failed to load medications. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (currentUser) {
      loadMedications();
    } else {
      setLoading(false);
    }
  }, [currentUser, loadMedications]);

  useEffect(() => {
    const permission = Notification.permission;
    if (permission === 'granted') {
      setNotificationStatus({ enabled: true, subscribed: true });
    } else if (permission === 'denied') {
      setNotificationStatus({ enabled: false, subscribed: false });
    }
  }, []);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(getCurrentTime12Hour());
    }, 60000);

    return () => clearInterval(interval);
  }, []);
  // Auto-refresh FCM token and set up foreground listener on every load
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;

    const init = async () => {
      const result = await initNotifications(currentUser);
      if (cancelled) return;
      if (result.granted && result.token) {
        setNotificationStatus({ enabled: true, subscribed: true });
      }
    };

    init();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Add new medication
  const handleAddMedication = async () => {
    if (!medName.trim() || !prescription.trim()) {
      setError('Please enter both medication name and prescription');
      return;
    }
  
    try {
      setError('');
      const newMed = await addMedication(medName, prescription, reminders);
      setMedications([newMed, ...medications]);
      setMedName('');
      setPrescription('');
    } catch (err) {
      setError(err.message || 'Failed to add medication');
    }
  };

  // Update medication
  const handleUpdateMedication = async () => {
    if (!editingId) return;

    try {
      setError('');
      const updatedData = await updateMedication(editingId, medName, prescription, reminders);
      
      setMedications(medications.map(med => 
        med.id === editingId 
          ? { ...med, ...updatedData }
          : med
      ));
      
      setMedName('');
      setPrescription('');
      setEditingId(null);
    } catch (err) {
      setError(err.message || 'Failed to update medication');
    }
  };

  // Toggle card expansion
  const toggleCardExpansion = (id, e) => {
    if (e) e.stopPropagation();
    
    setExpandedCards(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Open take modal
  const openTakeModal = (id, takeNow = true, e) => {
    if (e) e.stopPropagation();
    setSelectedMedId(id);
    setIsTakingNow(takeNow);
    
    if (takeNow) {
      setCustomTime('');
    } else {
      const now = new Date();
      const hours24 = now.getHours();
      const minutes = now.getMinutes();
      setCustomTime(`${hours24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
    }
    setShowTimeModal(true);
  };

  // Handle taking medication
  const handleTakeMedication = async () => {
    if (!selectedMedId) return;
  
    try {
      setError('');
      setIsLogging(true);
      
      let finalCustomTime = null;
      if (!isTakingNow && customTime) {
        const [hoursStr, minutesStr] = customTime.split(':');
        const hours24 = parseInt(hoursStr, 10);
        const minutes = parseInt(minutesStr, 10);
        
        let hours12 = hours24;
        let period = 'AM';
        
        if (hours24 >= 12) {
          period = 'PM';
          if (hours24 > 12) {
            hours12 = hours24 - 12;
          }
        } else if (hours24 === 0) {
          hours12 = 12;
        }
        
        finalCustomTime = `${hours12}:${minutesStr} ${period}`;
      }
      
      await takeMedication(selectedMedId, isTakingNow, finalCustomTime);
      
      const medName = medications.find(m => m.id === selectedMedId)?.name;
      setSuccessMedicationName(medName);
      setShowSuccess(true);
      
      closeModal();
      
      setTimeout(async () => {
        await loadMedications();
        setIsLogging(false);
      }, 500);
      
      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMedicationName('');
      }, 1000);
      
    } catch (err) {
      setIsLogging(false);
      setError(err.message || 'Failed to record medication');
    }
  };

  // Delete medication
  const handleDeleteMedication = async (id, e) => {
    if (e) e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this medication?')) return;

    try {
      setError('');
      await deleteMedication(id);
      setMedications(medications.filter(med => med.id !== id));
      setExpandedCards(prev => {
        const newExpanded = { ...prev };
        delete newExpanded[id];
        return newExpanded;
      });
      
      if (editingId === id) {
        setMedName('');
        setPrescription('');
        setEditingId(null);
      }
      
    } catch (err) {
      setError(err.message || 'Failed to delete medication');
    }
  };

  // Edit medication
  const startEditMedication = (med, e) => {
    if (e) e.stopPropagation();
    setMedName(med.name);
    setPrescription(med.prescription);
    setEditingId(med.id);
    setReminders(med.reminders ?? { before30Min: true, before10Min: true, atExactTime: true });
  };

  // Cancel edit
  const cancelEdit = () => {
    setMedName('');
    setPrescription('');
    setEditingId(null);
  };

  const closeModal = () => {
    setShowTimeModal(false);
    setSelectedMedId(null);
    setCustomTime('');
  };

  // Auto-update medications timer
  useEffect(() => {
    if (!loading && medications.length > 0) {
      const interval = setInterval(() => {
        setMedications(prevMeds => 
          prevMeds.map(medication => {
            if (medication.lastTaken) {
              const nextDose = calculateNextDose(medication.lastTaken, medication.intervalHours);
              const timeRemaining = calculateTimeRemaining(nextDose, medication.intervalHours, medication.lastTaken);
              
              return {
                ...medication,
                nextDose: nextDose ? new Date(nextDose) : null,
                timeRemaining,
                isOverdue: timeRemaining.isOverdue
              };
            }
            return medication;
          })
        );
      }, 60000);

      return () => clearInterval(interval);
    }
  }, [loading, medications.length]);

  // Get medication status for styling
  const getMedicationStatus = (med) => {
    if (!med.lastTaken) return 'not-started';
    
    const timeRemaining = calculateTimeRemaining(med.nextDose);
    if (timeRemaining.isOverdue) return 'missed';
    
    return 'taken';
  };

  // Helper to get display time with proper AM/PM
  const getDisplayTime = (time24) => {
    if (!time24) return '';
    
    const [hoursStr, minutesStr] = time24.split(':');
    const hours24 = parseInt(hoursStr, 10);
    let hours12 = hours24;
    let period = 'AM';
    
    if (hours24 >= 12) {
      period = 'PM';
      if (hours24 > 12) {
        hours12 = hours24 - 12;
      }
    } else if (hours24 === 0) {
      hours12 = 12;
    }
    
    return `${hours12}:${minutesStr} ${period}`;
  };

  // Render collapsed view
  const renderCollapsedView = (med, index) => {
    const timeRemaining = calculateTimeRemaining(med.nextDose);
    const status = getMedicationStatus(med);
    
    return (
      <div className="collapsed-view">
        <div className="collapsed-main">
          <div className="collapsed-name">
            <span className="medication-number-badge">{index + 1}</span>
            <span className="prescription-badge-small">{med.prescription}</span>
            <h3>{med.name}</h3>
          </div>
          <div className="collapsed-timer">
            {med.lastTaken ? (
              <div className={`timer-compact ${status === 'missed' ? 'overdue-timer' : ''} ${status === 'taken' ? 'taken-timer' : ''}`}>
<div className="timer-display">
  {timeRemaining.hours >= 24 && (
    <>
      <span className="timer-number">
        {Math.floor(timeRemaining.hours / 24)}
      </span>
      <span className="timer-label">DD</span>
      <span className="timer-separator">:</span>
    </>
  )}

  <span className="timer-number">
    {(timeRemaining.hours % 24).toString().padStart(2, '0')}
  </span>
  <span className="timer-label">HRS</span>

  <span className="timer-separator">:</span>

  <span className="timer-number">
    {timeRemaining.minutes.toString().padStart(2, '0')}
  </span>
  <span className="timer-label">MIN</span>
</div>
                {status === 'missed' && (
                  <div className="overdue-indicator">⚠️</div>
                )}
              </div>
            ) : (
              <div className="timer-placeholder-compact">
                Not started
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render expanded view
  const renderExpandedView = (med, index) => {
    const timeRemaining = calculateTimeRemaining(med.nextDose);
    const status = getMedicationStatus(med);
    const formattedHistory = getFormattedHistory(med.history);
    const timeSinceLastTaken = calculateTimeSinceLastTaken(med.lastTaken, med.history);
    
    return (
      <div className="expanded-view">
        <div className="expanded-header">
          <span className="medication-number-badge-large">#{index + 1}</span>
        </div>
        
        <div className="medication-info">
          <div className="info-row">
            <span className="label">Frequency:</span>
            <span>{getFrequencyDescription(med.timesPerDay)}</span>
          </div>
          <div className="info-row">
            <span className="label">Dose:</span>
            <span>{med.dosePerIntake} pill{med.dosePerIntake !== 1 ? 's' : ''} each time</span>
          </div>
          <div className="info-row">
            <span className="label">Interval:</span>
            <span>Every {med.intervalHours.toFixed(1)} hours</span>
          </div>
          {med.lastTaken && (
            <div className="info-row">
              <span className="label">Time since last taken:</span>
              <span className="time-since">
                {formatTimeSince(timeSinceLastTaken)}
              </span>
            </div>
          )}
          {med.nextDose && (
            <div className="info-row">
              <span className="label">Next dose at:</span>
              <span>{formatTimeWithDate(med.nextDose)}</span>
            </div>
          )}
        </div>

        <div className="timer-section">
          <h4>{status === 'missed' ? 'Overdue By:' : 'Next Dose In:'}</h4>
          {med.lastTaken ? (
            <div className={`timer ${status === 'missed' ? 'overdue-timer' : ''} ${status === 'taken' ? 'taken-timer' : ''}`}>
<div className="timer-display">
  {timeRemaining.hours >= 24 && (
    <>
      <span className="timer-number">
        {Math.floor(timeRemaining.hours / 24)}
      </span>
      <span className="timer-label">DD</span>
      <span className="timer-separator">:</span>
    </>
  )}

  <span className="timer-number">
    {(timeRemaining.hours % 24).toString().padStart(2, '0')}
  </span>
  <span className="timer-label">HRS</span>

  <span className="timer-separator">:</span>

  <span className="timer-number">
    {timeRemaining.minutes.toString().padStart(2, '0')}
  </span>
  <span className="timer-label">MIN</span>
</div>
              {status === 'missed' ? (
                <div className="overdue-alert">
                  ⚠️ Overdue! {timeRemaining.overdueMinutes > 60 
                    ? `${Math.floor(timeRemaining.overdueMinutes / 60)} hours ago`
                    : `${timeRemaining.overdueMinutes} minutes ago`}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="timer-placeholder">
              Not taken yet
            </div>
          )}
        </div>

        <div className="history-section">
          <h4>Recent History:</h4>
          {formattedHistory.length > 0 ? (
            <div className="history-list">
              {formattedHistory.slice(0, 3).map((entry, idx) => (
                <div key={idx} className="history-item">
                  <span className="history-date">
                    {entry.relativeDate}
                  </span>
                  <span className="history-time">
                    {entry.displayTime}
                  </span>
                  {entry.isCustomTime && (
                    <span className="history-note">(logged manually)</span>
                  )}
                </div>
              ))}
              {formattedHistory.length > 3 && (
                <div className="history-more">
                  + {formattedHistory.length - 3} more entries
                </div>
              )}
            </div>
          ) : (
            <div className="no-history">No history yet</div>
          )}
        </div>

        <div className="medication-actions">
          <div className="take-buttons">
            <button 
              onClick={(e) => openTakeModal(med.id, true, e)} 
              className="btn btn-take"
            >
              ✓ Take Now
            </button>
            <button 
              onClick={(e) => openTakeModal(med.id, false, e)} 
              className="btn btn-custom-time"
            >
              🕒 Log Time
            </button>
          </div>
          <div className="edit-actions">
            <button 
              onClick={(e) => startEditMedication(med, e)} 
              className="btn btn-edit"
            >
              ✎ Edit
            </button>
            <button 
              onClick={(e) => handleDeleteMedication(med.id, e)} 
              className="btn btn-delete"
            >
              🗑️ Delete
            </button>
          </div>
        </div>
      </div>
    );
  };


  // Show login message if not authenticated
  if (!currentUser) {
    return (
      <div className="trackmed-container">
        <div className="trackmed-content">
          <div className="empty-state">
            <p>Please log in to view your medications.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="trackmed-container" style={{userSelect:'none'}}>
      {/* Handle Header error gracefully */}
      {!headerError ? (
        <Header />
      ) : (
        <div className="simple-header">
          <h1>💊 Medication Tracker</h1>
        </div>
      )}

      <header className="trackmed-header">
        <h1>💊 Medication Tracker</h1>
        <div className="current-time-display">
          <span className="time-label">Current Time:</span>
          <span className="current-time">{currentTime}</span>
        </div>
      </header>

      <div className="trackmed-content">
        {error && (
          <div className="error-alert">
            {error}
            <button onClick={() => setError('')} className="error-close">×</button>
          </div>
        )}
        
        {showSuccess && (
          <div className="success-notification">
            <div className="success-content">
              <span className="success-icon">✅</span>
              <div className="success-message">
                <strong>{successMedicationName}</strong> logged successfully!
              </div>
            </div>
          </div>
        )}

        <div className="medications-list">
          <div className="list-stats">
            <div className="stats-header">
              <h3>Your Medications</h3>
              <div className="total-count">
                <span className="total-number">{medicationStats.total}</span>
                <span className="total-label">Total Medications</span>
              </div>
            </div>

          

  <button 
    className="test-notification-btn" 
    onClick={() => window.location.href = '/MedTracker.apk'}
  >
    📱 Install App (Download APK)
  </button>

            
            {/* {notificationStatus.enabled && notificationStatus.subscribed && (
              <div className="notification-status enabled">
                You will get ✅ Notifications to remind you.
              </div>
            )}

            {!notificationStatus.enabled && Notification.permission === 'denied' && (
              <div className="notification-status disabled">
                ❌ Notifications are blocked. Please enable them in browser settings.
              </div>
            )} */}

            {/* FIXED: Removed problematic inline styles */}
            <div className="stats-grid">
              <div className="stat-card taken">
                <div className="stat-card-content">
                  <div className="stat-icon">✓</div>
                  <div className="stat-card-info">
                    <span className="stat-card-value">{medicationStats.taken}</span>
                    <span className="stat-card-label">Taken</span>
                  </div>
                </div>
              </div>
              
              <div className="stat-card missed">
                <div className="stat-card-content">
                  <div className="stat-icon">⚠️</div>
                  <div className="stat-card-info">
                    <span className="stat-card-value">{medicationStats.missed}</span>
                    <span className="stat-card-label">Missed</span>
                  </div>
                </div>
              </div>
              
              <div className="stat-card not-started">
                <div className="stat-card-content">
                  <div className="stat-icon">⏳</div>
                  <div className="stat-card-info">
                    <span className="stat-card-value">{medicationStats.notStarted}</span>
                    <span className="stat-card-label">Not started</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading medications...</p>
            </div>
          ) : medications.length === 0 ? (
            <div className="empty-state">
              <p>No medications added yet. Add your first medication below.</p>
            </div>
          ) : (
            <div className="medications-grid">
              {medications.map((med, index) => {
                const isExpanded = expandedCards[med.id] || false;
                const status = getMedicationStatus(med);
                
                return (
                  <div 
                    key={med.id} 
                    className={`medication-card ${status} ${isExpanded ? 'expanded' : 'collapsed'}`}
                  >
                    <div 
                      className="card-header"
                      onClick={(e) => toggleCardExpansion(med.id, e)}
                    >
                      <div className="header-content">
                        {renderCollapsedView(med, index)}
                      </div>
                      <div className="expand-toggle">
                        <span className="toggle-icon">
                          {isExpanded ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>
                    
                    {isExpanded && renderExpandedView(med, index)}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className={`medication-form ${editingId ? 'editing-mode' : 'adding-mode'}`}>
          <h2 className="form-title">
            {editingId ? (
              <>
                <span className="form-icon">✎</span> Edit Medication
              </>
            ) : (
              <>
                <span className="form-icon">+</span> Add New Medication
              </>
            )}
          </h2>
          
          <div className="form-content">
            <div className="form-group">
              <label htmlFor="medName">Medication Name</label>
              <input
                type="text"
                id="medName"
                placeholder="e.g., Amoxicillin, Paracetamol"
                value={medName}
                onChange={(e) => setMedName(e.target.value)}
                className="form-input"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="prescription">Prescription</label>
              <input
                type="text"
                id="prescription"
                placeholder="e.g., 1x2 (1 pill, twice daily)"
                value={prescription}
                onChange={(e) => setPrescription(e.target.value)}
                className="form-input"
              />
              <PrescriptionInfo prescription={prescription} />
            </div>
            
            <div className="form-group">
              <label>Reminders</label>
              <div className="reminder-options">
                <label>
                  <input 
                    type="checkbox" 
                    checked={reminders.before30Min} 
                    onChange={(e) => setReminders(prev => ({ ...prev, before30Min: e.target.checked }))} 
                  />
                  30 minutes before
                </label>
                <label>
                  <input 
                    type="checkbox" 
                    checked={reminders.before10Min} 
                    onChange={(e) => setReminders(prev => ({ ...prev, before10Min: e.target.checked }))} 
                  />
                  10 minutes before
                </label>
              </div>
            </div>
            
            <div className="form-actions">
              {editingId ? (
                <>
                  <button 
                    onClick={handleUpdateMedication} 
                    className="btn btn-primary update-btn"
                    disabled={!medName.trim() || !prescription.trim()}
                  >
                    <span className="btn-icon">✓</span>
                    Update Medication
                  </button>
                  <button 
                    onClick={cancelEdit} 
                    className="btn btn-secondary cancel-btn"
                  >
                    <span className="btn-icon">✕</span>
                    Cancel
                  </button>
                </>
              ) : (
                <button 
                  onClick={handleAddMedication} 
                  className="btn btn-primary add-btn"
                  disabled={!medName.trim() || !prescription.trim()}
                >
                  <span className="btn-icon">➕</span>
                  Add Medication
                </button>
              )}
            </div>
          </div>
        </div>

        {showTimeModal && selectedMedId && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h3>
                  {isTakingNow 
                    ? `Take ${medications.find(m => m.id === selectedMedId)?.name} Now` 
                    : `Log Time for ${medications.find(m => m.id === selectedMedId)?.name}`
                  }
                </h3>
                <button onClick={closeModal} className="modal-close">×</button>
              </div>
              
              <div className="modal-content">
                {isTakingNow ? (
                  <div className="time-option">
                    <div className="time-display">
                      <span className="current-time">
                        {currentTime}
                      </span>
                      <span className="time-label">Current Time</span>
                    </div>
                    <p className="time-note">This will record the medication as taken right now.</p>
                  </div>
                ) : (
                  <>
                    <div className="custom-time-input">
                      <label htmlFor="customTime">Enter the time you took the medication:</label>
                      <div className="time-input-group">
                        <input
                          type="time"
                          id="customTime"
                          value={customTime}
                          onChange={(e) => setCustomTime(e.target.value)}
                          className="time-input"
                          required
                        />
                        {customTime && (
                          <div className="time-preview">
                            <span className="preview-label">Will log as:</span>
                            <span className="preview-time">{getDisplayTime(customTime)}</span>
                          </div>
                        )}
                      </div>
                      <p className="time-note">Current time: {currentTime}</p>
                    </div>

                    <div className="time-suggestion">
                      <p>Quick times:</p>
                      <div className="quick-times">
                        {['08:00 AM', '12:00 PM', '02:00 PM', '06:00 PM', '08:00 PM', '10:00 PM'].map((time) => (
                          <button
                            key={time}
                            onClick={() => {
                              const [timePart, period] = time.split(' ');
                              const [hours12, minutes] = timePart.split(':');
                              let hours24 = parseInt(hours12, 10);
                              
                              if (period === 'PM' && hours24 !== 12) {
                                hours24 += 12;
                              } else if (period === 'AM' && hours24 === 12) {
                                hours24 = 0;
                              }
                              
                              setCustomTime(`${hours24.toString().padStart(2, '0')}:${minutes}`);
                            }}
                            className="quick-time-btn"
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              <div className="modal-actions">
                <button 
                  onClick={handleTakeMedication} 
                  className="btn btn-primary modal-btn"
                  disabled={isLogging}
                >
                  {isLogging ? (
                    <>
                      <span className="spinner-small"></span> Please wait...
                    </>
                  ) : isTakingNow ? (
                    'Confirm Take Now'
                  ) : (
                    'Log Medication'
                  )}
                </button>
                <button onClick={closeModal} className="btn btn-secondary modal-btn">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackMed2;